// @flow
import { createApolloFetch } from "apollo-fetch"
import * as R from "ramda"

import mapLocationToPlace from "./mapLocationToPlace"
import type { Location } from "./locationTypes"
import { makeQuery } from "../query/queries"
import Place from "../../records/Place"

const DEFAULT_LIMIT_FOR_PLACES_FETCHED_IN_RADIUS = 150
const DEFAULT_RADIUS_SIZE_IN_KM = 250
const MAXIMUM_NUMBER_OF_CITIES = 9999

export type Params = {
  limit?: number,
  [key: string]: number | string | string[],
}

export type GraphQLParams = {
  [key: string]: { [key: string]: number | string },
}

const fstOrNull = locs => (locs.length > 0 ? mapLocationToPlace(locs[0]) : null)

const fetchLocationsFromGraphQL = (
  variables: GraphQLParams,
  graphqlApiUrl: string,
): Promise<Array<Location>> => {
  let withCountry = `country {
    locationId
    name
  }`
  let withCity = `city {
    locationId
    name
  }`
  switch (variables.options.locationType) {
    case "city":
      withCity = ""
      break
    case "country":
      withCountry = ""
      withCity = ""
      break
    default:
  }

  const query = `
  query allLocations($options: LocationsOptionsInput){
    allLocations(options: $options) {
      edges {
        node {
          locationId
          code
          name
          slug
          rank
          type
          isActive
          location {
            lat
            lng
          }
          ${withCity}
          ${withCountry}
        }
      }
    }
  }`

  const apolloFetch = createApolloFetch({ uri: graphqlApiUrl })

  return apolloFetch({ query, variables }).then(result => {
    const { data, errors } = result

    if (R.isNil(errors)) {
      const locations = data.allLocations.edges.map(item => {
        const { node } = item

        const country = R.isNil(node.country)
          ? null
          : { id: node.country.locationId, name: node.country.name }
        const city = R.isNil(node.city) ? null : { id: node.city.locationId }

        const location = {
          lat: node.location.lat,
          lon: node.location.lng,
        }

        return {
          id: node.locationId,
          name: node.name,
          rank: node.rank,
          slug: node.slug,
          type: node.type,
          code: node.code,
          location,
          active: node.isActive,
          country,
          city,
        }
      })
      return locations
    }

    return Promise.reject(`Invalid locations API response. Error: ${errors.message}`)
  })
}

const fetchLocationsByQuery = (base: string, query: string): Promise<Array<Location>> =>
  fetch(`${base + query}&X-Client=frontend`, {
    headers: {
      Accept: "application/json",
    },
  }).then(res => {
    if (res.ok) {
      return res.json().then(json => json.locations)
    }

    if (res.status === 404) {
      return []
    }

    return Promise.reject("Invalid locations API response")
  })

const fetchLocations = (base: string, params: Params): Promise<Array<Location>> =>
  fetchLocationsByQuery(base, makeQuery(params))

export async function fetchLocationPlaceId(
  base: string,
  locale: string,
  id: string,
): Promise<?Place> {
  const locs = await fetchLocations(base, {
    type: "id",
    id,
    locale,
  })

  return fstOrNull(locs)
}

export async function fetchLocationPlaceSlug(
  base: string,
  locale: string,
  term: string,
): Promise<?Place> {
  const locs = await fetchLocations(base, {
    type: "slug",
    term,
    locale,
  })

  return fstOrNull(locs)
}

export async function fetchLocationPlaceCode(
  base: string,
  locale: string,
  term: string,
): Promise<?Place> {
  const locs = await fetchLocations(base, {
    type: "term",
    term,
    locale,
  })

  return fstOrNull(locs)
}

export async function fetchByRadius(
  base: string,
  locale: string,
  lat: number,
  lon: number,
  radius: number = DEFAULT_RADIUS_SIZE_IN_KM,
  limit: number = DEFAULT_LIMIT_FOR_PLACES_FETCHED_IN_RADIUS,
): Promise<Array<Place>> {
  const locs = await fetchLocations(base, {
    type: "radius",
    sort: "rank",
    radius,
    locale,
    lat,
    lon,
    limit,
  })

  return locs.map(mapLocationToPlace)
}

export async function fetchCityByRadius(
  base: string,
  locale: string,
  lat: number,
  lon: number,
  radius: number = DEFAULT_RADIUS_SIZE_IN_KM,
): Promise<?Place> {
  const locs = await fetchLocations(base, {
    type: "radius",
    location_types: "city",
    limit: 1,
    radius,
    locale,
    lat,
    lon,
  })

  return fstOrNull(locs)
}

export async function fetchPopularCitiesByRadius(
  base: string,
  locale: string,
  lat: number,
  lon: number,
  radius: number = DEFAULT_RADIUS_SIZE_IN_KM,
): Promise<Place[]> {
  const locs = await fetchLocations(base, {
    type: "radius",
    location_types: "city",
    sort: "rank",
    limit: 20,
    radius,
    locale,
    lat,
    lon,
  })

  return locs.map(mapLocationToPlace)
}

export async function fetchBySlugRadius(
  base: string,
  locale: string,
  term: string,
  radius: number = DEFAULT_RADIUS_SIZE_IN_KM,
): Promise<Array<Place>> {
  const locs = await fetchLocations(base, {
    type: "radius",
    sort: "rank",
    radius,
    locale,
    term,
  })

  return locs.map(mapLocationToPlace)
}

export async function fetchAllCities(
  base: string,
  locale: string,
  apiAB: string,
  graphqlApiUrl: string,
): Promise<Array<Place>> {
  let locs
  if (apiAB === "GraphQLOn") {
    const localeToUnderscore = locale.replace("-", "_")
    locs = await fetchLocationsFromGraphQL(
      {
        options: {
          locationType: "city",
          locale: localeToUnderscore,
        },
      },
      graphqlApiUrl,
    )
  } else {
    locs = await fetchLocations(base, {
      type: "dump",
      location_types: "city",
      limit: MAXIMUM_NUMBER_OF_CITIES,
      locale,
    })
  }

  return locs.map(mapLocationToPlace)
}

export const fetchLocationPlacesByQuery = async (
  base: string,
  query: string,
): Promise<Array<Place>> => {
  const locs = await fetchLocationsByQuery(base, query)
  return locs.map(mapLocationToPlace)
}

export const fetchLocationPlaces = async (base: string, params: Params): Promise<Array<Place>> =>
  fetchLocationPlacesByQuery(base, makeQuery(params))
